import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronRight, Lock, LogOut, Plus, RefreshCcw, Save, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { slugify } from '../lib/service-utils';

const STORAGE_KEY = 'admin_services_password';

const parseLines = (value) =>
  value
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);

const toLines = (items = []) => items.join('\n');
const toCsv = (items = []) => items.join(', ');
const parseCsv = (value) =>
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const createEmptyService = (id) => ({
  id,
  slug: '',
  title: '',
  isActive: true,
  icon: 'Brain',
  image: '',
  shortDescription: '',
  fullDescription: '',
  benefits: [],
  deliveryMethods: [],
  duration: '',
  pricing: '',
});

const createEmptyAppointmentType = () => ({
  value: '',
  label: '',
  serviceSlug: '',
  isActive: true,
  durationMinutes: 60,
  availableWeekdays: [0, 1, 2, 3, 4, 5, 6],
  availableTimeSlots: ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'],
  priceAmount: '',
  currency: 'EUR',
});

const normalizeService = (service, fallbackId) => ({
  id: Number(service.id) || fallbackId,
  slug: slugify(String(service.slug || '').trim() || String(service.title || '').trim(), `service-${fallbackId}`),
  title: String(service.title || '').trim(),
  isActive: Boolean(service.isActive ?? true),
  icon: String(service.icon || 'Brain').trim() || 'Brain',
  image: String(service.image || '').trim(),
  shortDescription: String(service.shortDescription || '').trim(),
  fullDescription: String(service.fullDescription || '').trim(),
  benefits: Array.isArray(service.benefits)
    ? service.benefits.map((item) => String(item).trim()).filter(Boolean)
    : [],
  deliveryMethods: Array.isArray(service.deliveryMethods)
    ? service.deliveryMethods.map((item) => String(item).trim()).filter(Boolean)
    : [],
  duration: String(service.duration || '').trim(),
  pricing: String(service.pricing || '').trim(),
});

const normalizeAppointmentType = (item) => ({
  value: String(item.value || '').trim(),
  label: String(item.label || '').trim(),
  serviceSlug: slugify(String(item.serviceSlug || '').trim(), ''),
  isActive: Boolean(item.isActive ?? true),
  durationMinutes: Math.max(15, Math.round(Number(item.durationMinutes) || 60)),
  availableWeekdays: Array.isArray(item.availableWeekdays)
    ? item.availableWeekdays
        .map((value) => Number(value))
        .filter((value) => Number.isInteger(value) && value >= 0 && value <= 6)
    : [0, 1, 2, 3, 4, 5, 6],
  availableTimeSlots: Array.isArray(item.availableTimeSlots)
    ? item.availableTimeSlots
        .map((value) => {
          const match = String(value || '').trim().match(/^(\d{1,2})(?::(\d{1,2}))?$/);
          if (!match) {
            return '';
          }
          const hour = Number(match[1]);
          const minute = Number(match[2] || 0);
          if (Number.isNaN(hour) || Number.isNaN(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
            return '';
          }
          return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        })
        .filter(Boolean)
    : ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'],
  priceAmount: (() => {
    const rawValue = String(item.priceAmount ?? '').trim();
    if (!rawValue) {
      return null;
    }

    const parsed = Number(rawValue);
    if (Number.isNaN(parsed)) {
      return null;
    }

    return parsed;
  })(),
  currency: String(item.currency || 'EUR')
    .replace(/[^A-Za-z]/g, '')
    .toUpperCase()
    .slice(0, 3) || 'EUR',
});

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [expandedServiceIndex, setExpandedServiceIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingServiceIndex, setUploadingServiceIndex] = useState(null);
  const [status, setStatus] = useState({ type: '', text: '' });

  const [adminPassword, setAdminPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);

  const nextServiceId = useMemo(() => {
    const maxId = services.reduce((max, service) => {
      const id = Number(service.id) || 0;
      return id > max ? id : max;
    }, 0);

    return maxId + 1;
  }, [services]);

  const verifyPassword = useCallback(async (password, silent = false) => {
    if (!password) {
      if (!silent) {
        setStatus({ type: 'error', text: 'Enter your admin password.' });
      }
      setIsCheckingAuth(false);
      setIsAuthenticated(false);
      return false;
    }

    setIsVerifying(true);

    try {
      const response = await fetch('/api/admin/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        throw new Error(`Auth failed (${response.status})`);
      }

      sessionStorage.setItem(STORAGE_KEY, password);
      setAdminPassword(password);
      setIsAuthenticated(true);
      setStatus({ type: '', text: '' });
      return true;
    } catch (error) {
      console.error(error);
      sessionStorage.removeItem(STORAGE_KEY);
      setIsAuthenticated(false);
      if (!silent) {
        setStatus({ type: 'error', text: 'Invalid admin password.' });
      }
      return false;
    } finally {
      setIsVerifying(false);
      setIsCheckingAuth(false);
    }
  }, []);

  const loadContent = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await fetch('/api/content/services', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load services content (${response.status})`);
      }

      const payload = await response.json();
      const loadedServices = Array.isArray(payload?.services)
        ? payload.services.map((service, index) => ({
            ...service,
            slug: String(service?.slug || '').trim() || slugify(String(service?.title || ''), `service-${index + 1}`),
            isActive: Boolean(service?.isActive ?? true),
          }))
        : [];

      setServices(loadedServices);
      setExpandedServiceIndex((current) => {
        if (loadedServices.length === 0) {
          return null;
        }
        if (current === null || current >= loadedServices.length) {
          return 0;
        }
        return current;
      });

      setAppointmentTypes(
        Array.isArray(payload?.appointmentTypes)
          ? payload.appointmentTypes.map((item) => ({
              ...item,
              serviceSlug: String(item?.serviceSlug || '').trim(),
              isActive: Boolean(item?.isActive ?? true),
              durationMinutes: Number(item?.durationMinutes) || 60,
              availableWeekdays: Array.isArray(item?.availableWeekdays)
                ? item.availableWeekdays
                : [0, 1, 2, 3, 4, 5, 6],
              availableTimeSlots: Array.isArray(item?.availableTimeSlots)
                ? item.availableTimeSlots
                : ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'],
              priceAmount: item?.priceAmount ?? '',
              currency: item?.currency || 'EUR',
            }))
          : [],
      );
    } catch (error) {
      console.error(error);
      setStatus({
        type: 'error',
        text: 'Failed to load content from backend. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const storedPassword = sessionStorage.getItem(STORAGE_KEY);

    if (!storedPassword) {
      setIsCheckingAuth(false);
      return;
    }

    setAdminPassword(storedPassword);
    verifyPassword(storedPassword, true);
  }, [verifyPassword]);

  useEffect(() => {
    if (isAuthenticated) {
      loadContent();
    }
  }, [isAuthenticated, loadContent]);

  const updateServiceField = (index, field, value) => {
    setServices((current) =>
      current.map((service, serviceIndex) =>
        serviceIndex === index ? { ...service, [field]: value } : service,
      ),
    );
  };

  const updateAppointmentTypeField = (index, field, value) => {
    setAppointmentTypes((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item,
      ),
    );
  };

  const addService = () => {
    setServices((current) => {
      const nextServices = [...current, createEmptyService(nextServiceId)];
      setExpandedServiceIndex(nextServices.length - 1);
      return nextServices;
    });
  };

  const removeService = (index) => {
    setServices((current) => {
      const nextServices = current.filter((_, serviceIndex) => serviceIndex !== index);
      setExpandedServiceIndex((openIndex) => {
        if (openIndex === null) {
          return null;
        }
        if (openIndex === index) {
          return nextServices.length ? Math.min(index, nextServices.length - 1) : null;
        }
        if (openIndex > index) {
          return openIndex - 1;
        }
        return openIndex;
      });
      return nextServices;
    });
  };

  const addAppointmentType = () => {
    setAppointmentTypes((current) => [...current, createEmptyAppointmentType()]);
  };

  const removeAppointmentType = (index) => {
    setAppointmentTypes((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const uploadAdminImage = useCallback(
    async (file) => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/upload-image', {
        method: 'POST',
        headers: {
          'X-Admin-Password': adminPassword,
        },
        body: formData,
      });

      if (!response.ok) {
        if (response.status === 401) {
          sessionStorage.removeItem(STORAGE_KEY);
          setIsAuthenticated(false);
          throw new Error('Admin session expired. Please sign in again.');
        }

        const errorPayload = await response.json().catch(() => null);
        throw new Error(errorPayload?.detail || `Image upload failed (${response.status})`);
      }

      return response.json();
    },
    [adminPassword],
  );

  const handleServiceImageUpload = async (index, event) => {
    const imageFile = event.target.files?.[0];
    if (!imageFile) {
      return;
    }

    setUploadingServiceIndex(index);
    try {
      const payload = await uploadAdminImage(imageFile);
      updateServiceField(index, 'image', payload.url || '');
      setStatus({ type: 'success', text: `Service #${index + 1} image uploaded.` });
    } catch (error) {
      console.error(error);
      setStatus({
        type: 'error',
        text: error?.message || 'Failed to upload service image.',
      });
    } finally {
      setUploadingServiceIndex(null);
      event.target.value = '';
    }
  };

  const saveContent = async () => {
    const normalizedServices = services.map((service, index) =>
      normalizeService(service, index + 1),
    );
    const normalizedAppointmentTypes = appointmentTypes.map(normalizeAppointmentType);

    if (normalizedServices.length === 0) {
      setStatus({ type: 'error', text: 'Add at least one service before saving.' });
      return;
    }

    if (normalizedAppointmentTypes.length === 0) {
      setStatus({ type: 'error', text: 'Add at least one appointment type before saving.' });
      return;
    }

    const hasEmptyService = normalizedServices.some(
      (service) =>
        !service.title ||
        !service.shortDescription ||
        !service.fullDescription ||
        service.benefits.length === 0 ||
        service.deliveryMethods.length === 0,
    );

    if (hasEmptyService) {
      setStatus({
        type: 'error',
        text: 'Each service needs title, short/full description, benefits and delivery methods.',
      });
      return;
    }

    const hasEmptyAppointmentType = normalizedAppointmentTypes.some(
      (item) => !item.value || !item.label,
    );

    if (hasEmptyAppointmentType) {
      setStatus({
        type: 'error',
        text: 'Each appointment type needs both value and label.',
      });
      return;
    }

    const invalidAppointmentSchedule = normalizedAppointmentTypes.some(
      (item) =>
        !item.serviceSlug ||
        !Array.isArray(item.availableTimeSlots) ||
        item.availableTimeSlots.length === 0 ||
        !Array.isArray(item.availableWeekdays) ||
        item.availableWeekdays.length === 0,
    );

    if (invalidAppointmentSchedule) {
      setStatus({
        type: 'error',
        text: 'Each appointment type must be linked to a service with at least one weekday and one time slot.',
      });
      return;
    }

    setIsSaving(true);
    setStatus({ type: '', text: '' });

    try {
      const response = await fetch('/api/content/services', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Admin-Password': adminPassword,
        },
        body: JSON.stringify({
          services: normalizedServices,
          appointmentTypes: normalizedAppointmentTypes,
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          sessionStorage.removeItem(STORAGE_KEY);
          setIsAuthenticated(false);
          setStatus({ type: 'error', text: 'Admin session expired. Please sign in again.' });
          return;
        }

        throw new Error(`Failed to save services content (${response.status})`);
      }

      const payload = await response.json();
      const savedServices = Array.isArray(payload?.services)
        ? payload.services.map((service, index) => ({
            ...service,
            slug: String(service?.slug || '').trim() || slugify(String(service?.title || ''), `service-${index + 1}`),
            isActive: Boolean(service?.isActive ?? true),
          }))
        : [];

      setServices(savedServices);
      setExpandedServiceIndex((current) => {
        if (savedServices.length === 0) {
          return null;
        }
        if (current === null || current >= savedServices.length) {
          return 0;
        }
        return current;
      });

      setAppointmentTypes(
        Array.isArray(payload?.appointmentTypes)
          ? payload.appointmentTypes.map((item) => ({
              ...item,
              serviceSlug: String(item?.serviceSlug || '').trim(),
              isActive: Boolean(item?.isActive ?? true),
              durationMinutes: Number(item?.durationMinutes) || 60,
              availableWeekdays: Array.isArray(item?.availableWeekdays)
                ? item.availableWeekdays
                : [0, 1, 2, 3, 4, 5, 6],
              availableTimeSlots: Array.isArray(item?.availableTimeSlots)
                ? item.availableTimeSlots
                : ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'],
              priceAmount: item?.priceAmount ?? '',
              currency: item?.currency || 'EUR',
            }))
          : [],
      );
      setStatus({ type: 'success', text: 'Saved successfully.' });
    } catch (error) {
      console.error(error);
      setStatus({
        type: 'error',
        text: 'Failed to save content. Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSignIn = async (event) => {
    event.preventDefault();
    await verifyPassword(adminPassword, false);
  };

  const handleSignOut = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setIsAuthenticated(false);
    setAdminPassword('');
    setServices([]);
    setAppointmentTypes([]);
    setStatus({ type: '', text: '' });
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-sage-50 py-12">
        <div className="container mx-auto px-4 max-w-lg">
          <Card className="border-sage-200">
            <CardContent className="p-6 text-sage-700">Checking admin session...</CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-sage-50 py-12">
        <div className="container mx-auto px-4 max-w-lg">
          <Card className="border-sage-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sage-900">
                <Lock className="w-5 h-5" />
                Admin Access
              </CardTitle>
              <CardDescription>
                Enter the admin password to edit services.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSignIn} className="space-y-4">
                <div>
                  <Label className="text-sage-700">Password</Label>
                  <Input
                    type="password"
                    value={adminPassword}
                    onChange={(event) => setAdminPassword(event.target.value)}
                    className="mt-1.5 border-sage-300"
                    autoFocus
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-sage-600 hover:bg-sage-700"
                  disabled={isVerifying}
                >
                  {isVerifying ? 'Verifying...' : 'Sign In'}
                </Button>
                {status.text && (
                  <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                    {status.text}
                  </p>
                )}
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sage-50 py-12">
      <div className="container mx-auto px-4 max-w-6xl space-y-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-sage-900">Services Admin</h1>
            <p className="text-sage-600 mt-1">
              Edit services and booking options shown on your website.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Link to="/admin">
              <Button type="button" variant="outline" className="border-sage-300">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Admin Home
              </Button>
            </Link>
            <Button
              type="button"
              variant="outline"
              onClick={handleSignOut}
              className="border-sage-300"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={loadContent}
              disabled={isLoading || isSaving}
              className="border-sage-300"
            >
              <RefreshCcw className="w-4 h-4 mr-2" />
              Reload
            </Button>
            <Button
              type="button"
              onClick={saveContent}
              disabled={isLoading || isSaving}
              className="bg-sage-600 hover:bg-sage-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {status.text && (
          <div
            className={`rounded-lg px-4 py-3 text-sm font-medium ${
              status.type === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}
          >
            {status.text}
          </div>
        )}

        <Card className="border-sage-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between bg-white border-b border-sage-200">
            <div>
              <CardTitle className="text-sage-900">Services</CardTitle>
              <CardDescription>
                These cards appear on Home and Services pages.
              </CardDescription>
            </div>
            <Button type="button" variant="outline" onClick={addService} className="border-sage-300">
              <Plus className="w-4 h-4 mr-2" />
              Add Service
            </Button>
          </CardHeader>
          <CardContent className="space-y-6 bg-white pt-6">
            {services.length === 0 && !isLoading && (
              <p className="text-sage-600 text-sm">No services yet. Click “Add Service”.</p>
            )}

            {services.map((service, index) => {
              const isOpen = expandedServiceIndex === index;
              const displayTitle = service.title || `Service #${index + 1}`;
              const displaySlug = service.slug ? `/${service.slug}` : 'No slug set';
              const displayPrice = service.pricing || 'No price text';
              const displayState = service.isActive ? 'Active' : 'Disabled';

              return (
                <div key={`service-${index}`} className="rounded-lg border border-sage-200 bg-white">
                  <div className="flex flex-wrap items-center justify-between gap-3 p-4">
                    <button
                      type="button"
                      className="flex min-w-0 flex-1 items-center gap-3 text-left"
                      onClick={() => setExpandedServiceIndex(isOpen ? null : index)}
                    >
                      {isOpen ? (
                        <ChevronDown className="h-4 w-4 text-sage-600 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-sage-600 flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="font-semibold text-sage-900 truncate">{displayTitle}</p>
                        <p className="text-xs text-sage-500 truncate">{displaySlug}</p>
                        <p className="text-xs text-sage-600 truncate">{displayPrice}</p>
                        <p className="text-xs text-sage-600 truncate">{displayState}</p>
                      </div>
                    </button>

                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="border-sage-300"
                        onClick={() => setExpandedServiceIndex(isOpen ? null : index)}
                      >
                        {isOpen ? 'Close' : 'Open'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="border-red-200 text-red-700 hover:bg-red-50"
                        onClick={() => removeService(index)}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Remove
                      </Button>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="space-y-4 border-t border-sage-200 p-4">
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <Label className="text-sage-700">ID</Label>
                          <Input
                            type="number"
                            value={service.id ?? ''}
                            onChange={(event) =>
                              updateServiceField(index, 'id', Number(event.target.value) || '')
                            }
                            className="mt-1.5 border-sage-300"
                          />
                        </div>
                        <div>
                          <Label className="text-sage-700">Title</Label>
                          <Input
                            value={service.title || ''}
                            onChange={(event) =>
                              updateServiceField(index, 'title', event.target.value)
                            }
                            className="mt-1.5 border-sage-300"
                          />
                        </div>
                      </div>

                      <label className="inline-flex items-center gap-2 rounded-md border border-sage-200 px-3 py-2">
                        <input
                          type="checkbox"
                          checked={Boolean(service.isActive)}
                          onChange={(event) =>
                            updateServiceField(index, 'isActive', event.target.checked)
                          }
                        />
                        <span className="text-sm text-sage-800">Service is active</span>
                      </label>

                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <Label className="text-sage-700">Slug (used in service detail URL)</Label>
                          <Input
                            value={service.slug || ''}
                            onChange={(event) =>
                              updateServiceField(index, 'slug', event.target.value)
                            }
                            className="mt-1.5 border-sage-300"
                            placeholder="mental-health-coaching"
                          />
                        </div>
                        <div>
                          <Label className="text-sage-700">Icon (Brain/Heart/Users)</Label>
                          <Input
                            value={service.icon || ''}
                            onChange={(event) =>
                              updateServiceField(index, 'icon', event.target.value)
                            }
                            className="mt-1.5 border-sage-300"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-sage-700">Image URL</Label>
                        <Input
                          value={service.image || ''}
                          onChange={(event) => updateServiceField(index, 'image', event.target.value)}
                          className="mt-1.5 border-sage-300"
                        />
                        <div className="mt-3">
                          <Label className="text-sage-700">Upload Image File</Label>
                          <Input
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            onChange={(event) => handleServiceImageUpload(index, event)}
                            className="mt-1.5 border-sage-300"
                            disabled={uploadingServiceIndex === index || isSaving}
                          />
                          <p className="mt-1 text-xs text-sage-500">
                            {uploadingServiceIndex === index
                              ? 'Uploading image...'
                              : 'Allowed: JPG, PNG, WEBP, GIF. Max 5MB.'}
                          </p>
                        </div>
                        {service.image && (
                          <div className="mt-3 max-w-md overflow-hidden rounded-md border border-sage-200">
                            <img src={service.image} alt={`${service.title || 'Service'} preview`} className="h-44 w-full object-cover" />
                          </div>
                        )}
                      </div>

                      <div>
                        <Label className="text-sage-700">Short Description</Label>
                        <Textarea
                          value={service.shortDescription || ''}
                          onChange={(event) =>
                            updateServiceField(index, 'shortDescription', event.target.value)
                          }
                          className="mt-1.5 min-h-20 border-sage-300"
                        />
                      </div>

                      <div>
                        <Label className="text-sage-700">Full Description</Label>
                        <Textarea
                          value={service.fullDescription || ''}
                          onChange={(event) =>
                            updateServiceField(index, 'fullDescription', event.target.value)
                          }
                          className="mt-1.5 min-h-28 border-sage-300"
                        />
                      </div>

                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <Label className="text-sage-700">Benefits (one per line)</Label>
                          <Textarea
                            value={toLines(service.benefits)}
                            onChange={(event) =>
                              updateServiceField(index, 'benefits', parseLines(event.target.value))
                            }
                            className="mt-1.5 min-h-24 border-sage-300"
                          />
                        </div>
                        <div>
                          <Label className="text-sage-700">Delivery Methods (one per line)</Label>
                          <Textarea
                            value={toLines(service.deliveryMethods)}
                            onChange={(event) =>
                              updateServiceField(
                                index,
                                'deliveryMethods',
                                parseLines(event.target.value),
                              )
                            }
                            className="mt-1.5 min-h-24 border-sage-300"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <Label className="text-sage-700">Duration</Label>
                          <Input
                            value={service.duration || ''}
                            onChange={(event) =>
                              updateServiceField(index, 'duration', event.target.value)
                            }
                            className="mt-1.5 border-sage-300"
                          />
                        </div>
                        <div>
                          <Label className="text-sage-700">Pricing</Label>
                          <Input
                            value={service.pricing || ''}
                            onChange={(event) =>
                              updateServiceField(index, 'pricing', event.target.value)
                            }
                            className="mt-1.5 border-sage-300"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card className="border-sage-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between bg-white border-b border-sage-200">
            <div>
              <CardTitle className="text-sage-900">Appointment Types</CardTitle>
              <CardDescription>
                These options show in the booking form service dropdown.
              </CardDescription>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={addAppointmentType}
              className="border-sage-300"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Type
            </Button>
          </CardHeader>
          <CardContent className="space-y-4 bg-white pt-6">
            {appointmentTypes.map((item, index) => (
              <div key={`appointment-${index}`} className="rounded-lg border border-sage-200 p-4 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-semibold text-sage-900">
                    Booking Type #{index + 1}
                  </h3>
                  <div className="flex items-center gap-2">
                    <label className="inline-flex items-center gap-2 text-sm text-sage-800">
                      <input
                        type="checkbox"
                        checked={Boolean(item.isActive)}
                        onChange={(event) =>
                          updateAppointmentTypeField(index, 'isActive', event.target.checked)
                        }
                      />
                      Active
                    </label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-red-200 text-red-700 hover:bg-red-50"
                      onClick={() => removeAppointmentType(index)}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Remove
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sage-700">Value</Label>
                    <Input
                      value={item.value || ''}
                      onChange={(event) =>
                        updateAppointmentTypeField(index, 'value', event.target.value)
                      }
                      className="mt-1.5 border-sage-300"
                    />
                  </div>
                  <div>
                    <Label className="text-sage-700">Label</Label>
                    <Input
                      value={item.label || ''}
                      onChange={(event) =>
                        updateAppointmentTypeField(index, 'label', event.target.value)
                      }
                      className="mt-1.5 border-sage-300"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-sage-700">Linked Service Slug</Label>
                    <select
                      value={item.serviceSlug || ''}
                      onChange={(event) =>
                        updateAppointmentTypeField(index, 'serviceSlug', event.target.value)
                      }
                      className="mt-1.5 w-full rounded-md border border-sage-300 bg-white px-3 py-2 text-sm"
                    >
                      <option value="">Select service</option>
                      {services.map((service) => (
                        <option key={`service-option-${service.slug || service.id}`} value={service.slug || ''}>
                          {(service.title || service.slug || `Service ${service.id}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-sage-700">Duration (minutes)</Label>
                    <Input
                      type="number"
                      min="15"
                      step="5"
                      value={item.durationMinutes ?? 60}
                      onChange={(event) =>
                        updateAppointmentTypeField(index, 'durationMinutes', event.target.value)
                      }
                      className="mt-1.5 border-sage-300"
                    />
                  </div>
                  <div>
                    <Label className="text-sage-700">Price Amount</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.priceAmount ?? ''}
                      onChange={(event) =>
                        updateAppointmentTypeField(index, 'priceAmount', event.target.value)
                      }
                      className="mt-1.5 border-sage-300"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-sage-700">Currency</Label>
                    <Input
                      value={item.currency || 'EUR'}
                      onChange={(event) =>
                        updateAppointmentTypeField(index, 'currency', event.target.value)
                      }
                      className="mt-1.5 border-sage-300"
                      placeholder="EUR"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-sage-700">Available Weekdays (0=Mon ... 6=Sun)</Label>
                    <Input
                      value={toCsv(Array.isArray(item.availableWeekdays) ? item.availableWeekdays : [])}
                      onChange={(event) =>
                        updateAppointmentTypeField(
                          index,
                          'availableWeekdays',
                          parseCsv(event.target.value)
                            .map((value) => Number(value))
                            .filter((value) => Number.isInteger(value)),
                        )
                      }
                      className="mt-1.5 border-sage-300"
                      placeholder="0,1,2,3,4,5"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-sage-700">Available Time Slots (comma-separated HH:MM)</Label>
                  <Textarea
                    value={toCsv(Array.isArray(item.availableTimeSlots) ? item.availableTimeSlots : [])}
                    onChange={(event) =>
                      updateAppointmentTypeField(index, 'availableTimeSlots', parseCsv(event.target.value))
                    }
                    className="mt-1.5 min-h-20 border-sage-300"
                    placeholder="08:00, 09:00, 10:00, 11:00"
                  />
                </div>
              </div>
            ))}

            {appointmentTypes.length === 0 && !isLoading && (
              <p className="text-sage-600 text-sm">No appointment types yet. Click “Add Type”.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminServices;
